import {
  applyChoiceChange,
  findOtherOption,
  isOtherChosen,
  otherTextKey,
  resolveFieldMaxLength,
  resolveFieldDisplayStyle,
  type FieldDef,
} from '@citizen-walk/shared-types';
import { OtherTextInput, RatingScaleButtons } from '@citizen-walk/shared-ui';
import { getRenderablePostFields } from './postFieldsHelpers';

/**
 * 投稿フォームの extraFields 描画コンポーネント (Sprint BG-DataSettings 5)。
 *
 * 旧 v1 `old/apps/user/src/pages/MainPage.tsx` post composer (L399-495 + L548-676) の
 * postFields 駆動 widget 群を踏襲。`UserFieldsForm.tsx` (Sprint 4) の 9 type renderer を
 * 参考に、Post 用に 10 type 全て (text / textarea / select / multiselect / number /
 * checkbox / date / rating-scale + 除外する photo / location) を扱う。
 *
 * 設計原則:
 *   - 値は `Record<string, unknown>` で flat に管理 (旧 Post.extraFields と同形)
 *   - PostForm 本体が直接 handle する builtin (title/comment/location/photo/category/detail)
 *     と photo/location type は `getRenderablePostFields` で予め除外して受け取る
 *   - displayOrder 昇順 sort + mode='hidden' skip (UserFieldsForm.ordered と同 logic)
 *   - Tailwind only、inline style 禁止 (CLAUDE.md デザインシステム)
 *
 * docs/105 §① FieldDisplayStyle 対応 (各フィールドの「見た目」を admin が選べる):
 *   - `resolveFieldDisplayStyle(field)` (top-level displayStyle 優先 → 旧 optionsMeta.displayStyle
 *     を後方互換で吸収 → 型既定) で描画スタイルを解決し分岐する。old2 PostTab.renderGenericField 踏襲。
 *   - 実装した型×スタイル:
 *       text       : text-input(既定) / textarea
 *       textarea   : textarea(既定) / text-input
 *       number     : number-input(既定) / slider / stepper
 *       checkbox   : checkbox(既定) / toggle
 *       select     : dropdown(既定) / buttons / radio
 *       multiselect: checkboxes(既定) / buttons / dropdown
 *       rating-scale: buttons(既定) / slider / stars
 *       date       : date-input(唯一)
 *   - 未設定なら型既定 = 現行の見た目を維持 (回帰なし)。未知の組合せは既定スタイルへ fallback。
 *
 * 選択肢「その他」の記入欄 (2026-10-06、`fieldOtherText.ts`):
 *   - ラジオ / チェックボックスは「□その他」の行の後ろに記入欄を並べ、ボタン / プルダウンは
 *     「その他」を選んだときだけ下に出す。記入し始めたら「その他」を自動で選ぶ。
 *   - 記入内容は `values['{key}__other']`、「その他」を外すと消える (`applyChoiceChange`)。
 */

export interface PostExtraFieldsFormProps {
  /** district.dataSettings.postFields (`resolvePostFields` 経由で取得済を渡す)。 */
  fields: FieldDef[];
  /** 値辞書 (key === FieldDef.key)、初期は `{}`。 */
  values: Record<string, unknown>;
  /** 値変更コールバック。next は新しい dict 全体 (PostForm 側で setState)。 */
  onChange: (next: Record<string, unknown>) => void;
  /** submit 中等の disable。 */
  disabled?: boolean;
  /**
   * Sprint BG-DataSettings 27 — 親 Card が title を提供するときに重複描画を避けるため
   * 内部 `<label>` (labelNode) と outer `<div>` の縦余白を抑制する。
   * PostForm が各 extra field を個別 Card で包む構成 (per-field 連番) で使用。
   */
  hideLabel?: boolean;
}

export function PostExtraFieldsForm({
  fields,
  values,
  onChange,
  disabled,
  hideLabel,
}: PostExtraFieldsFormProps) {
  const ordered = getRenderablePostFields(fields);

  function setValue(key: string, v: unknown) {
    onChange({ ...values, [key]: v });
  }

  /** select / multiselect の選択値を変える (「その他」を外したら記入内容も消す)。 */
  function setChoice(field: FieldDef, next: unknown) {
    onChange(applyChoiceChange(values, field, next));
  }

  /** 「その他」の記入欄。記入し始めたら「その他」を選んだ状態にする。 */
  function setOtherText(field: FieldDef, otherValue: string, text: string) {
    const current = values[field.key];
    let nextChoice: unknown = current;
    if (text !== '' && !isOtherChosen(otherValue, current)) {
      nextChoice =
        field.type === 'multiselect'
          ? [...(Array.isArray(current) ? (current as string[]) : []), otherValue]
          : otherValue;
    }
    onChange({ ...applyChoiceChange(values, field, nextChoice), [otherTextKey(field.key)]: text });
  }

  function otherTextOf(field: FieldDef): string {
    const v = values[otherTextKey(field.key)];
    return typeof v === 'string' ? v : '';
  }

  if (ordered.length === 0) return null;

  return (
    <div className={hideLabel ? '' : 'space-y-4'}>
      {ordered.map((field) => {
        const required = field.mode === 'required';
        const value = values[field.key];
        const labelNode = hideLabel ? null : (
          <label className="block mb-1 font-medium text-sm" htmlFor={`pf-${field.key}`}>
            {field.label} {required && <span className="text-danger-600">*</span>}
          </label>
        );
        const description = field.description ? (
          <p className="text-xs text-slate-500 mb-1">{field.description}</p>
        ) : null;

        // docs/105 §①: 表示スタイルを解決 (top-level displayStyle → 旧 optionsMeta → 型既定)。
        const style = resolveFieldDisplayStyle(field);

        if (field.type === 'text' || field.type === 'textarea') {
          // Sprint BG-DataSettings 27: maxLength を optionsMeta から取得 (admin 編集可、データ駆動)。
          // docs/105 §①: text/textarea は displayStyle で 1 行入力 / 複数行を切替。
          const max = resolveFieldMaxLength(field);
          const strVal = (value as string) ?? '';
          const counter =
            max !== null ? (
              <small className="text-[11px] text-slate-400 mt-0.5 block">
                {strVal.length} / {max}
              </small>
            ) : null;
          if (style === 'text-input') {
            return (
              <div key={field.key}>
                {labelNode}
                {description}
                <input
                  id={`pf-${field.key}`}
                  type="text"
                  value={strVal}
                  onChange={(e) => setValue(field.key, e.target.value)}
                  disabled={disabled}
                  maxLength={max ?? undefined}
                  className="w-full min-h-touch px-3 border border-slate-300 rounded-md text-sm disabled:bg-slate-100"
                />
                {counter}
              </div>
            );
          }
          // textarea (複数行)
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              <textarea
                id={`pf-${field.key}`}
                value={strVal}
                onChange={(e) => setValue(field.key, e.target.value)}
                disabled={disabled}
                maxLength={max ?? undefined}
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm disabled:bg-slate-100"
              />
              {counter}
            </div>
          );
        }

        if (field.type === 'select') {
          // docs/105 §①: displayStyle で UI を切替 (dropdown / buttons / radio、後方互換 checkboxes)。
          const strVal = (value as string) ?? '';
          const other = findOtherOption(field);
          const otherInput = (inline: boolean) =>
            other ? (
              <OtherTextInput
                inline={inline}
                value={otherTextOf(field)}
                onChange={(t) => setOtherText(field, other.value, t)}
                ariaLabel={`${field.label}（${other.label}の内容）`}
                disabled={disabled}
              />
            ) : null;
          if (style === 'buttons') {
            return (
              <div key={field.key}>
                {labelNode}
                {description}
                <div role="radiogroup" className="flex flex-wrap items-center gap-2">
                  {field.options.map((opt) => {
                    const active = strVal === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        disabled={disabled}
                        onClick={() => setChoice(field, active ? '' : opt.value)}
                        className={`min-h-touch px-3 rounded-md border text-sm font-medium transition-colors disabled:opacity-50 ${
                          active
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {other && strVal === other.value && otherInput(false)}
              </div>
            );
          }
          if (style === 'radio' || style === 'checkboxes') {
            // 'radio' (新) / 'checkboxes' (旧 optionsMeta 後方互換): 縦並びラジオ。single-select なので 1 つだけ active。
            return (
              <div key={field.key}>
                {labelNode}
                {description}
                <div className="grid gap-1 border border-slate-200 rounded-md p-3">
                  {field.options.map((opt) => {
                    const checked = strVal === opt.value;
                    const radio = (
                      <label key={opt.value} className="flex items-center gap-2 py-1 shrink-0">
                        <input
                          type="radio"
                          name={`pf-${field.key}`}
                          checked={checked}
                          onChange={() => setChoice(field, opt.value)}
                          disabled={disabled}
                          className="w-5 h-5"
                        />
                        <span className="text-sm">{opt.label}</span>
                      </label>
                    );
                    if (other && opt.value === other.value) {
                      return (
                        <div key={opt.value} className="flex items-center gap-2">
                          {radio}
                          {otherInput(true)}
                        </div>
                      );
                    }
                    return radio;
                  })}
                </div>
              </div>
            );
          }
          // default: dropdown
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              <select
                id={`pf-${field.key}`}
                value={strVal}
                onChange={(e) => setChoice(field, e.target.value)}
                disabled={disabled}
                className="w-full min-h-touch px-3 border border-slate-300 rounded-md text-sm bg-white disabled:bg-slate-100"
              >
                <option value="">未選択</option>
                {field.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {other && strVal === other.value && otherInput(false)}
            </div>
          );
        }

        if (field.type === 'multiselect') {
          // docs/105 §①: checkboxes(既定) / buttons / dropdown。`style` は resolveFieldDisplayStyle 由来。
          const arr = Array.isArray(value) ? (value as string[]) : [];
          const other = findOtherOption(field);
          const otherChosen = other !== null && arr.includes(other.value);
          const otherInput = (inline: boolean) =>
            other ? (
              <OtherTextInput
                inline={inline}
                value={otherTextOf(field)}
                onChange={(t) => setOtherText(field, other.value, t)}
                ariaLabel={`${field.label}（${other.label}の内容）`}
                disabled={disabled}
              />
            ) : null;
          if (style === 'buttons') {
            return (
              <div key={field.key}>
                {labelNode}
                {description}
                <div className="flex flex-wrap items-center gap-2">
                  {field.options.map((opt) => {
                    const active = arr.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        aria-pressed={active}
                        disabled={disabled}
                        onClick={() => {
                          const next = active
                            ? arr.filter((x) => x !== opt.value)
                            : [...arr, opt.value];
                          setChoice(field, next);
                        }}
                        className={`min-h-touch px-3 rounded-md border text-sm font-medium transition-colors disabled:opacity-50 ${
                          active
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {otherChosen && otherInput(false)}
              </div>
            );
          }
          if (style === 'dropdown') {
            // multiselect の dropdown: native multi-select (Ctrl/Cmd + click)。
            return (
              <div key={field.key}>
                {labelNode}
                {description}
                <select
                  id={`pf-${field.key}`}
                  multiple
                  value={arr}
                  onChange={(e) => {
                    const next = Array.from(e.target.selectedOptions).map((o) => o.value);
                    setChoice(field, next);
                  }}
                  disabled={disabled}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white disabled:bg-slate-100"
                  size={Math.min(6, field.options.length)}
                >
                  {field.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {otherChosen && otherInput(false)}
              </div>
            );
          }
          // default: checkboxes
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              <div className="grid gap-1 border border-slate-200 rounded-md p-3">
                {field.options.map((opt) => {
                  const checked = arr.includes(opt.value);
                  const box = (
                    <label key={opt.value} className="flex items-center gap-2 py-1 shrink-0">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...arr, opt.value]
                            : arr.filter((x) => x !== opt.value);
                          setChoice(field, next);
                        }}
                        disabled={disabled}
                        className="w-5 h-5"
                      />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  );
                  if (other && opt.value === other.value) {
                    return (
                      <div key={opt.value} className="flex items-center gap-2">
                        {box}
                        {otherInput(true)}
                      </div>
                    );
                  }
                  return box;
                })}
              </div>
            </div>
          );
        }

        if (field.type === 'number') {
          // docs/105 §①: number-input(既定) / slider / stepper。old2 PostTab.renderGenericField 踏襲。
          const num = typeof value === 'number' ? value : null;
          if (style === 'slider') {
            const min = field.optionsMeta?.min ?? 0;
            const max = field.optionsMeta?.max ?? 100;
            const current = num ?? min;
            return (
              <div key={field.key}>
                {labelNode}
                {description}
                <div className="flex items-center gap-3">
                  <input
                    id={`pf-${field.key}`}
                    type="range"
                    min={min}
                    max={max}
                    value={current}
                    onChange={(e) => setValue(field.key, Number(e.target.value))}
                    disabled={disabled}
                    className="flex-1 disabled:opacity-50"
                  />
                  <span className="min-w-[3rem] text-right text-sm font-mono">{current}</span>
                </div>
              </div>
            );
          }
          if (style === 'stepper') {
            const min = field.optionsMeta?.min;
            const max = field.optionsMeta?.max;
            const cur = num ?? 0;
            const clamp = (v: number) => {
              if (typeof min === 'number' && v < min) return min;
              if (typeof max === 'number' && v > max) return max;
              return v;
            };
            return (
              <div key={field.key}>
                {labelNode}
                {description}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="減らす"
                    onClick={() => setValue(field.key, clamp(cur - 1))}
                    disabled={disabled}
                    className="w-10 h-10 border border-slate-300 rounded text-lg disabled:opacity-50"
                  >
                    −
                  </button>
                  <input
                    id={`pf-${field.key}`}
                    type="number"
                    value={num === null ? '' : String(num)}
                    onChange={(e) =>
                      setValue(field.key, e.target.value === '' ? null : Number(e.target.value))
                    }
                    disabled={disabled}
                    className="w-20 min-h-touch px-3 border border-slate-300 rounded text-center text-sm disabled:bg-slate-100"
                  />
                  <button
                    type="button"
                    aria-label="増やす"
                    onClick={() => setValue(field.key, clamp(cur + 1))}
                    disabled={disabled}
                    className="w-10 h-10 border border-slate-300 rounded text-lg disabled:opacity-50"
                  >
                    ＋
                  </button>
                </div>
              </div>
            );
          }
          // default: number-input
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              <input
                id={`pf-${field.key}`}
                type="number"
                value={num === null ? '' : String(num)}
                onChange={(e) => {
                  const n = e.target.value === '' ? null : Number(e.target.value);
                  setValue(field.key, n);
                }}
                disabled={disabled}
                className="w-full min-h-touch px-3 border border-slate-300 rounded-md text-sm disabled:bg-slate-100"
              />
            </div>
          );
        }

        if (field.type === 'rating-scale') {
          // docs/105 §①: buttons(既定) / slider / stars。old2 PostTab.renderGenericField 踏襲。
          const min = field.optionsMeta?.min ?? -3;
          const max = field.optionsMeta?.max ?? 3;
          const excludeZero = field.optionsMeta?.excludeZero === true;
          const posLabel = field.optionsMeta?.scaleLabels?.posLabel;
          const negLabel = field.optionsMeta?.scaleLabels?.negLabel;
          const steps: number[] = [];
          for (let n = min; n <= max; n++) {
            if (excludeZero && n === 0) continue;
            steps.push(n);
          }
          const selected = typeof value === 'number' ? value : undefined;
          // hideLabel (PostForm の Card が見出しを出す) でも radiogroup の aria-labelledby 先は要るので、
          // 消さずに読み上げ専用 (sr-only) にする。見えるまま残すと Card 見出しと二重表示になる。
          const labelHeader = (
            <div
              className={hideLabel ? 'sr-only' : 'block mb-1 font-medium text-sm'}
              id={`pf-${field.key}-label`}
            >
              {field.label} {required && <span className="text-danger-600">*</span>}
            </div>
          );
          const scaleLabelFooter =
            negLabel || posLabel ? (
              <div className="mt-1.5 flex items-start justify-between gap-3 text-xs leading-snug text-slate-500">
                <span className="min-w-0 break-words text-left">{negLabel ?? ''}</span>
                <span className="min-w-0 break-words text-right">{posLabel ?? ''}</span>
              </div>
            ) : null;

          if (style === 'slider' && steps.length > 0) {
            const sMin = steps[0] as number;
            const sMax = steps[steps.length - 1] as number;
            const current = selected ?? sMin;
            return (
              <div key={field.key}>
                {labelHeader}
                {description}
                <div className="flex items-center gap-3">
                  {negLabel && (
                    <span className="text-xs text-slate-500 shrink-0">← {negLabel}</span>
                  )}
                  <input
                    id={`pf-${field.key}`}
                    type="range"
                    min={sMin}
                    max={sMax}
                    step={1}
                    value={current}
                    onChange={(e) => setValue(field.key, Number(e.target.value))}
                    disabled={disabled}
                    className="flex-1 disabled:opacity-50"
                  />
                  {posLabel && (
                    <span className="text-xs text-slate-500 shrink-0">{posLabel} →</span>
                  )}
                </div>
                <div className="text-center text-sm font-mono mt-1">
                  {selected === undefined ? '（未選択）' : current > 0 ? `+${current}` : current}
                </div>
              </div>
            );
          }

          if (style === 'stars') {
            // 正の値範囲のみ ★ で描画。負値を含む scale では stars が成立しないので buttons へ fallback。
            const positives = steps.filter((v) => v > 0);
            if (positives.length > 0) {
              return (
                <div key={field.key}>
                  {labelHeader}
                  {description}
                  {/* 星の列と両端の言葉を同じ幅の箱に入れ、言葉を星の左端・右端に揃える。 */}
                  <div className="w-fit max-w-full">
                    <div className="flex items-center gap-1">
                      {positives.map((v) => {
                        const active = selected !== undefined && selected >= v;
                        return (
                          <button
                            key={v}
                            type="button"
                            aria-label={`${v} つ星`}
                            aria-pressed={active}
                            disabled={disabled}
                            onClick={() => setValue(field.key, selected === v ? null : v)}
                            className={`text-2xl disabled:opacity-50 ${
                              active ? 'text-amber-500' : 'text-slate-300'
                            }`}
                          >
                            ★
                          </button>
                        );
                      })}
                      <span className="ml-2 text-sm text-slate-500">{selected ?? '－'}</span>
                    </div>
                    {scaleLabelFooter}
                  </div>
                </div>
              );
            }
            // 負値を含む → 既定 buttons へ fallback (下の return)
          }

          // default: buttons
          return (
            <div key={field.key}>
              {labelHeader}
              {description}
              <RatingScaleButtons
                steps={steps}
                selected={selected}
                onSelect={(n) => setValue(field.key, n)}
                negLabel={negLabel}
                posLabel={posLabel}
                disabled={disabled}
                labelledBy={`pf-${field.key}-label`}
              />
            </div>
          );
        }

        if (field.type === 'date') {
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              <input
                id={`pf-${field.key}`}
                type="date"
                value={(value as string) ?? ''}
                onChange={(e) => setValue(field.key, e.target.value)}
                disabled={disabled}
                className="w-full min-h-touch px-3 border border-slate-300 rounded-md text-sm disabled:bg-slate-100"
              />
            </div>
          );
        }

        if (field.type === 'checkbox') {
          // docs/105 §①: checkbox(既定) / toggle。old2 PostTab.renderGenericField 踏襲。
          const on = Boolean(value);
          if (style === 'toggle') {
            return (
              <div key={field.key}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-label={field.label}
                  disabled={disabled}
                  onClick={() => setValue(field.key, !on)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full border transition disabled:opacity-50 ${
                    on
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-slate-700 border-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block w-10 h-6 rounded-full relative ${
                      on ? 'bg-white/30' : 'bg-slate-200'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 ${
                        on ? 'right-0.5' : 'left-0.5'
                      } w-5 h-5 rounded-full bg-white shadow transition-all`}
                    />
                  </span>
                  <span className="text-sm">
                    {field.label} {required && <span className="text-danger-600">*</span>}
                  </span>
                </button>
                {description}
              </div>
            );
          }
          return (
            <div key={field.key}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) => setValue(field.key, e.target.checked)}
                  disabled={disabled}
                  className="w-5 h-5"
                />
                <span className="text-sm">
                  {field.label} {required && <span className="text-danger-600">*</span>}
                </span>
              </label>
              {description}
            </div>
          );
        }

        // photo / location は本コンポーネントでは描画しない (PostForm 本体側 UI、旧 v1 仕様)
        return null;
      })}
    </div>
  );
}
